import React, { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_ACCESS_GROUPS } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import AccessGroupReportColumn from "../../../models/ColumnDefinitions/AccessGroupReportColumn";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import AccessAreaSearch from "../../../models/search/AccessAreaSearch";
import AccessGroupReportSearch from "../../../models/search/AccessGroupReportSearch";
import AccessTimePatternSearch from "../../../models/search/AccessTimePatternSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { getAccessAreaNamesWithPagination } from "../../../services/accessAreaService";
import { getAccessTimePatternNamesWithPagination } from "../../../services/accessTimePatternService";
import { auditRunReport } from "../../../services/auditService";
import { getAccessGroupReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { ListQuery } from "@/models/ListQuery";

interface AccessGroupReportProps {
    reportId: string;
}

const AccessGroupReport: FC<AccessGroupReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const lookup: Lookup[] = [
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNamesWithPagination,
            searchAccessor: AccessTimePatternSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.groupType,
            values: [
                { value: "1", label: translate.byKey(TranslationKeys.accessGroup) },
                { value: "2", label: translate.byKey(TranslationKeys.floorGroup) }
            ],
            searchAccessor: AccessGroupReportSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ];

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_ACCESS_GROUPS;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, [], reportId) => void) => {
        setDetails(userLoginId, currentMenuDetail, [], reportId);
    }
    // <-- end context

    const getDataMethod = async (listParameters?: any) => {
        try {
            await auditRunReport(reportId);
            return getAccessGroupReport(translate, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return (<TableContextProvider
        dataMethod={(listParameters) => getDataMethod(listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={AccessGroupReportColumn}
        searchDescriptor={AccessGroupReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.accessGroups)}
            selectRows={false}
            cyName="accessGroupReport">
        </TableTab>
    </TableContextProvider >
    );
};

export default AccessGroupReport;
